import { interpolate, spring, useVideoConfig } from 'remotion';

// Types
import type { Scene5bCardData } from '../../types';

// Constants
import { CLAMP_BOTH } from '@/constants';
import {
  SCENE5B_ANIMATION,
  SCENE5B_AVATAR_START_FRAME,
  SCENE5B_COLORS,
  SCENE5B_LAYOUT,
  SCENE5B_TIMING,
} from '../../constants';

// Components
import { FallbackRemotionImg } from '@/components/common/FallbackRemotionImg';

// Spring-based avatar motion values for staggered entrance
const getAvatarMotion = (frame: number, fps: number, index: number) => {
  const { avatar } = SCENE5B_ANIMATION;

  // Stagger each avatar's entrance by its index
  const startFrame =
    SCENE5B_AVATAR_START_FRAME + index * SCENE5B_TIMING.avatarStagger;

  // Spring curve from 0 to 1 starting at each avatar's delayed frame
  const progress = spring({
    frame: frame - startFrame,
    fps,
    durationInFrames: SCENE5B_TIMING.avatarDuration,
    config: avatar.spring,
  });

  return {
    opacity: interpolate(
      progress,
      avatar.opacityStops,
      avatar.opacity,
      CLAMP_BOTH,
    ), // Fade in following custom opacity stops
    translateX: interpolate(progress, [0, 1], avatar.translateX, CLAMP_BOTH), // Slide horizontally into position
    translateY: interpolate(progress, [0, 1], avatar.translateY, CLAMP_BOTH), // Slide vertically into position
    scale: interpolate(progress, [0, 1], avatar.scale, CLAMP_BOTH), // Scale up from small to full size
  };
};

interface TrainingSupportersProps {
  card: Scene5bCardData;
  frame: number;
}

export const TrainingSupporters = ({
  card,
  frame,
}: TrainingSupportersProps) => {
  const { fps } = useVideoConfig();
  const {
    top,
    size,
    border,
    containerLeft,
    gap: avatarGap,
  } = SCENE5B_LAYOUT.avatar;
  const gap = card.avatars.length > 1 ? avatarGap : 0;

  return (
    <div
      className="absolute flex origin-left"
      style={{ top, left: containerLeft, gap: `${gap}px` }}
    >
      {card.avatars.map(({ src = '', name = '' }, index) => {
        const { opacity, translateX, translateY, scale } = getAvatarMotion(
          frame,
          fps,
          index,
        );

        return (
          <div
            key={`${card.id}-supporter-${index}`}
            className="origin-center"
            style={{
              width: size,
              height: size,
              opacity, // Spring-driven fade in
              transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`, // Combined slide + zoom entrance
            }}
          >
            <div
              className="size-full overflow-hidden rounded-full box-border border-solid"
              style={{
                backgroundColor: SCENE5B_COLORS.avatarFallback,
                borderWidth: border,
                borderColor: SCENE5B_COLORS.avatarRing,
              }}
            >
              <FallbackRemotionImg
                src={src}
                alt={name}
                className="size-full object-cover"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
